import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { api, ApiError } from '@/lib/api'
import { useAuth } from '@/lib/auth'
import { useCountry } from '@/lib/country'
import { dateTime } from '@/lib/format'
import { CHANNEL_LABELS, isOpenLead, leadStatusTone, waitLabel, waitTone, type WaitTone } from '@/lib/leads'
import { profileFacts, safeSourceHref, TIER_LABELS, tierTone } from '@/lib/leadTiers'
import { useAsync } from '@/lib/useAsync'
import { useUnsavedChanges } from '@/lib/useUnsavedChanges'
import { LeaveGuard, useLeaveGuard } from '@/components/LeaveGuard'
import { PicklistField } from '@/components/PicklistField'
import { PageHeader, Tabs } from '@/components/ui/page'
import { Badge, Busy, Button, Card, CardHeader, EmptyState, ErrorState, Loading, cx } from '@/components/ui/primitives'
import { Dialog } from '@/components/ui/dialog'
import { Checkbox, Field, FormGrid, Input, SaveActions, Select, Textarea } from '@/components/ui/form'
import { DEFAULT_PAGE_SIZE, FilterSelect, ListToolbar, Pagination } from '@/components/ui/listing'
import { SortableTh, Table, Td, Th, Tr } from '@/components/ui/table'
import { useToast } from '@/components/ui/toast'
import type {
  ConvertLeadResult,
  LeadActivityItem,
  LeadActivityType,
  LeadAssignee,
  LeadChannel,
  LeadCounts,
  LeadCustomerMatch,
  LeadDetail,
  LeadDuplicate,
  LeadListItem,
  LeadStatus,
  Paged,
  ReferenceItem,
} from '@/types/api'

/**
 * The leads inbox: everybody who might buy, before they are a customer.
 *
 * <p>
 * Tabs answer whose leads these are; the status filter answers how far along. Junk is a tab
 * of its own rather than a status option, because spam in the ordinary listing is exactly
 * the noise the status exists to remove - it appears only when somebody goes looking.
 * </p>
 */

type View = 'mine' | 'unassigned' | 'all' | 'junk'

const DEFAULT_SORT = '-created'

const WAIT_CLASSES: Record<WaitTone, string> = {
  ok: 'text-pos',
  soon: 'text-warn',
  late: 'text-crit font-semibold',
}

export function Leads() {
  const { can, scope } = useAuth()
  const { current } = useCountry()
  const navigate = useNavigate()
  const toast = useToast()

  // A rep's scope is their own leads, and the unassigned queue is not theirs to see - the
  // API answers it with nothing, so the tab would only ever be empty.
  const seesQueue = scope('lead.view') !== 'own'

  const [view, setView] = useState<View>('mine')
  const [status, setStatus] = useState('open')
  const [channel, setChannel] = useState('')
  const [search, setSearch] = useState('')
  const [sort, setSort] = useState(DEFAULT_SORT)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)
  const [creating, setCreating] = useState(false)

  const counts = useAsync<LeadCounts>(
    (signal) => api.get<LeadCounts>('/leads/counts', { countryId: current?.countryId }, signal),
    [current?.countryId],
  )

  const list = useAsync<Paged<LeadListItem>>(
    (signal) =>
      api.get<Paged<LeadListItem>>(
        '/leads',
        {
          countryId: current?.countryId,
          view: view === 'junk' ? 'all' : view,
          status: view === 'junk' ? 'Junk' : status,
          channel,
          search,
          sort,
          page,
          pageSize,
        },
        signal,
      ),
    [current?.countryId, view, status, channel, search, sort, page, pageSize],
  )

  function refine(apply: () => void) {
    apply()
    setPage(1)
  }

  const threshold = counts.data?.waitingMinutes ?? 60
  const now = new Date()
  const rows = list.data?.items ?? []

  return (
    <>
      <PageHeader
        eyebrow={current?.name}
        title="Leads"
        hint={`Everybody who has asked, before they are a customer. A new lead nobody has answered in ${threshold} minutes turns red, and its owner is reminded.`}
        actions={
          can('lead.create') && (
            <Button variant="primary" onClick={() => setCreating(true)}>
              New lead
            </Button>
          )
        }
      />

      <Tabs<View>
        active={view}
        onSelect={(next) => refine(() => setView(next))}
        tabs={[
          { id: 'mine', label: 'Mine', count: counts.data?.mine },
          ...(seesQueue ? [{ id: 'unassigned' as const, label: 'Unassigned', count: counts.data?.unassigned }] : []),
          { id: 'all', label: seesQueue ? 'All' : 'All of mine' },
          { id: 'junk', label: 'Junk' },
        ]}
      />

      {(counts.data?.waiting ?? 0) > 0 && (
        <p className="mb-4 rounded-md border border-crit/30 bg-crit-wash px-4 py-2.5 text-[13px] text-crit" role="status">
          {counts.data?.waiting} new lead{counts.data?.waiting === 1 ? ' has' : 's have'} waited more than {threshold}{' '}
          minutes with nothing logged.
        </p>
      )}

      <ListToolbar
        sort={sort}
        onSortChange={(next) => refine(() => setSort(next))}
        sortChoices={[
          { value: '-created', label: 'Newest first' },
          { value: 'created', label: 'Oldest first' },
          { value: 'name', label: 'Name A–Z' },
          { value: 'company', label: 'Company A–Z' },
          { value: 'status', label: 'Status' },
        ]}
      >
        <div className="w-72">
          <Input
            placeholder="Search name, company, email, phone"
            value={search}
            onChange={(event) => refine(() => setSearch(event.target.value))}
          />
        </div>

        {view !== 'junk' && (
          <FilterSelect label="Status" value={status} onChange={(next) => refine(() => setStatus(next))}>
            <option value="open">Open</option>
            <option value="New">New</option>
            <option value="Working">Working</option>
            <option value="Qualified">Qualified</option>
            <option value="closed">Converted or disqualified</option>
            <option value="all">Everything but junk</option>
          </FilterSelect>
        )}

        <FilterSelect label="Channel" value={channel} width="w-36" onChange={(next) => refine(() => setChannel(next))}>
          <option value="">Any</option>
          {(Object.keys(CHANNEL_LABELS) as LeadChannel[]).map((key) => (
            <option key={key} value={key}>
              {CHANNEL_LABELS[key]}
            </option>
          ))}
        </FilterSelect>
      </ListToolbar>

      {list.error && <ErrorState title={list.error.title} detail={list.error.detail} onRetry={list.reload} />}

      <Busy busy={list.loading}>
        {list.data && (
          <Card>
            {rows.length === 0 ? (
              <EmptyState
                title={view === 'junk' ? 'No junk' : 'No leads here'}
                hint={
                  view === 'mine'
                    ? 'Leads assigned to you appear here the moment they arrive, from a contact form or typed in by hand.'
                    : 'Nothing matches those filters in this country.'
                }
              />
            ) : (
              <Table>
                <thead>
                  <tr>
                    <SortableTh field="name" sort={sort} onSortChange={(next) => refine(() => setSort(next || DEFAULT_SORT))}>
                      Lead
                    </SortableTh>
                    <Th>Channel</Th>
                    <Th>Came from</Th>
                    <Th>Owner</Th>
                    <SortableTh field="status" sort={sort} onSortChange={(next) => refine(() => setSort(next || DEFAULT_SORT))}>
                      Status
                    </SortableTh>
                    <SortableTh field="created" sort={sort} onSortChange={(next) => refine(() => setSort(next || DEFAULT_SORT))}>
                      Waiting
                    </SortableTh>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((lead) => {
                    const tone = waitTone(lead, threshold, now)

                    return (
                      <Tr key={lead.id} onClick={() => navigate(`/leads/${lead.id}`)}>
                        <Td>
                          <span className="font-medium">{lead.fullName}</span>
                          {lead.isTest && (
                            <span className="ml-2 align-middle">
                              <Badge tone="warning">Test</Badge>
                            </span>
                          )}
                          {lead.tier && (
                            <span className="ml-2 align-middle">
                              <Badge tone={tierTone(lead.tier)}>{TIER_LABELS[lead.tier]}</Badge>
                            </span>
                          )}
                          <span className="block text-[12px] text-muted">
                            {[lead.companyName, lead.countryIsoCode].filter(Boolean).join(' · ')}
                          </span>
                        </Td>
                        <Td>
                          <ChannelBadge channel={lead.channel} />
                        </Td>
                        <Td className="text-muted">
                          {lead.sourceName ?? '-'}
                          {lead.campaignName && <span className="block text-[12px] text-faint">{lead.campaignName}</span>}
                        </Td>
                        <Td
                          className={
                            lead.ownerName ? 'text-muted' : lead.assignmentPending ? 'text-muted italic' : 'font-medium text-warn'
                          }
                        >
                          {/* Nobody has it yet because the tier decides who will - not because
                              it was missed. Said differently from Unassigned for that reason. */}
                          {lead.ownerName ?? (lead.assignmentPending ? 'Researching…' : 'Unassigned')}
                        </Td>
                        <Td>
                          <Badge tone={leadStatusTone(lead.status)}>{lead.status}</Badge>
                        </Td>
                        <Td className="tabular">
                          {tone ? (
                            <span className={WAIT_CLASSES[tone]}>{waitLabel(lead.createdAt, now)}</span>
                          ) : (
                            <span className="text-faint">{lead.firstRespondedAt ? 'answered' : '-'}</span>
                          )}
                        </Td>
                      </Tr>
                    )
                  })}
                </tbody>
              </Table>
            )}

            <Pagination
              page={list.data.page}
              pageSize={list.data.pageSize}
              totalCount={list.data.totalCount}
              totalPages={list.data.totalPages}
              onPageChange={setPage}
              onPageSizeChange={(next) => refine(() => setPageSize(next))}
            />
          </Card>
        )}
      </Busy>

      <NewLeadDialog
        open={creating}
        onClose={() => setCreating(false)}
        onCreated={(id, name) => {
          setCreating(false)
          toast.show(`${name} added`, 'success')
          navigate(`/leads/${id}`)
        }}
      />
    </>
  )
}

/**
 * Each paid channel has its own tone, so a column of Meta and Google leads reads at a glance;
 * the two Codemax-owned doors stay quiet.
 */
const CHANNEL_TONE: Record<LeadChannel, 'neutral' | 'positive' | 'accent' | 'warning'> = {
  Manual: 'neutral',
  WebForm: 'neutral',
  MetaAds: 'accent',
  GoogleAds: 'positive',
}

function ChannelBadge({ channel }: { channel: LeadChannel }) {
  return <Badge tone={CHANNEL_TONE[channel]}>{CHANNEL_LABELS[channel]}</Badge>
}

/**
 * A lead typed in by hand - a phone call, a walk-in, a business card.
 *
 * <p>
 * The duplicate check runs while the email and phone are typed, against what the caller can
 * see, so the second copy of somebody is never made in the first place. It is a hint, not a
 * refusal: two people sharing a front desk number are not the same lead.
 * </p>
 */
function NewLeadDialog({
  open,
  onClose,
  onCreated,
}: {
  open: boolean
  onClose: () => void
  onCreated: (id: string, name: string) => void
}) {
  const toast = useToast()
  const { can, user } = useAuth()
  const { current } = useCountry()
  const [form, setForm] = useState({
    fullName: '',
    companyName: '',
    email: '',
    phone: '',
    jobTitle: '',
    message: '',
  })
  const [leadSourceId, setLeadSourceId] = useState<string | null>(null)
  const [ownerUserId, setOwnerUserId] = useState('')
  const [saving, setSaving] = useState(false)
  const [duplicates, setDuplicates] = useState<LeadDuplicate[]>([])

  const countryId = current?.countryId
  const mayChoose = can('lead.assign') || can('lead.assign.any')

  const assignees = useAsync<LeadAssignee[]>(
    (signal) =>
      open && mayChoose && countryId
        ? api.get<LeadAssignee[]>('/leads/assignees', { countryId }, signal)
        : Promise.resolve([]),
    [open, mayChoose, countryId],
  )

  // Debounced: a request per keystroke is a request per keystroke, and the answer is only
  // worth showing once somebody has stopped typing.
  useEffect(() => {
    if (!open || !countryId || (!form.email.trim() && form.phone.trim().length < 6)) {
      setDuplicates([])
      return
    }

    const controller = new AbortController()
    const timer = window.setTimeout(() => {
      api
        .get<LeadDuplicate[]>(
          '/leads/duplicates',
          { countryId, email: form.email.trim(), phone: form.phone.trim() },
          controller.signal,
        )
        .then(setDuplicates)
        .catch(() => setDuplicates([]))
    }, 400)

    return () => {
      window.clearTimeout(timer)
      controller.abort()
    }
  }, [open, countryId, form.email, form.phone])

  function set(key: keyof typeof form, value: string) {
    setForm((currentForm) => ({ ...currentForm, [key]: value }))
  }

  function reset() {
    setForm({ fullName: '', companyName: '', email: '', phone: '', jobTitle: '', message: '' })
    setLeadSourceId(null)
    setOwnerUserId('')
    setDuplicates([])
  }

  async function create() {
    if (!countryId) return

    setSaving(true)
    try {
      const created = await api.post<{ id: string }>('/leads', {
        countryId,
        fullName: form.fullName,
        companyName: form.companyName || null,
        email: form.email || null,
        phone: form.phone || null,
        jobTitle: form.jobTitle || null,
        message: form.message || null,
        leadSourceId,

        // Omitted rather than sent as the caller's own id, which the API defaults to anyway.
        ownerUserId: mayChoose && ownerUserId ? ownerUserId : null,
      })

      onCreated(created.id, form.fullName)
      reset()
    } catch (cause) {
      toast.show(cause instanceof ApiError ? cause.message : 'Could not add the lead', 'error')
    } finally {
      setSaving(false)
    }
  }

  const contactable = form.email.trim() !== '' || form.phone.trim() !== ''

  return (
    <Dialog
      open={open}
      title="New lead"
      description={`Created in ${current?.name ?? 'the country you are working in'}.`}
      onClose={onClose}
      footer={
        <>
          <Button size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            size="sm"
            variant="primary"
            loading={saving}
            disabled={!form.fullName.trim() || !contactable || !countryId}
            onClick={() => void create()}
          >
            Add lead
          </Button>
        </>
      }
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Name" required>
          {(id) => <Input id={id} value={form.fullName} onChange={(e) => set('fullName', e.target.value)} />}
        </Field>
        <Field label="Company">
          {(id) => <Input id={id} value={form.companyName} onChange={(e) => set('companyName', e.target.value)} />}
        </Field>
        <Field label="Email" hint="An email or a phone number is required.">
          {(id) => <Input id={id} type="email" value={form.email} onChange={(e) => set('email', e.target.value)} />}
        </Field>
        <Field label="Phone">
          {(id) => <Input id={id} type="tel" value={form.phone} onChange={(e) => set('phone', e.target.value)} />}
        </Field>
        <Field label="Job title">
          {(id) => <Input id={id} value={form.jobTitle} onChange={(e) => set('jobTitle', e.target.value)} />}
        </Field>
        <PicklistField
          label="Source of lead"
          list="LeadSource"
          countryId={countryId}
          value={leadSourceId}
          onChange={setLeadSourceId}
        />
        <div className="sm:col-span-2">
          <Field label="What they asked about">
            {(id) => <Textarea id={id} rows={3} value={form.message} onChange={(e) => set('message', e.target.value)} />}
          </Field>
        </div>

        {mayChoose ? (
          <Field label="Owner" hint="Defaults to you. Only people who can open leads in this country.">
            {(id) => (
              <Select id={id} value={ownerUserId} onChange={(e) => setOwnerUserId(e.target.value)}>
                <option value="">Me ({user?.displayName})</option>
                {(assignees.data ?? [])
                  .filter((person) => person.userId !== user?.id)
                  .map((person) => (
                    <option key={person.userId} value={person.userId}>
                      {person.displayName}
                    </option>
                  ))}
              </Select>
            )}
          </Field>
        ) : (
          <Field label="Owner" hint="Leads you create are yours. A manager can move one later.">
            {(id) => <Input id={id} disabled value={user?.displayName ?? ''} />}
          </Field>
        )}
      </div>

      {duplicates.length > 0 && (
        <div className="mt-4 rounded-md border border-warn/30 bg-warn-wash px-4 py-3 text-[13px]" role="status">
          <p className="font-medium text-warn">Somebody with these details is already on file</p>
          <ul className="mt-1.5 space-y-1 text-ink-2">
            {duplicates.map((match) => (
              <li key={`${match.kind}-${match.id}`}>
                <Link
                  to={match.kind === 'lead' ? `/leads/${match.id}` : `/customers/${match.id}`}
                  className="text-accent underline-offset-2 hover:underline"
                >
                  {match.name}
                  {match.companyName && ` (${match.companyName})`}
                </Link>{' '}
                <span className="text-muted">
                  - {match.kind === 'lead' ? `a ${match.status?.toLowerCase()} lead` : 'a customer contact'}
                  {match.ownerName && `, ${match.ownerName}'s`}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </Dialog>
  )
}

/** What the lead's own form edits, and therefore what an unsaved change can be about. */
const EDITED = ['fullName', 'companyName', 'email', 'phone', 'jobTitle', 'message', 'leadSourceId'] as const

export function LeadDetailPage() {
  const { id } = useParams()
  return id ? <Lead leadId={id} /> : <ErrorState title="No lead" detail="That link is missing a lead id." />
}

type Pending = 'activity' | 'status' | 'assign' | 'convert' | 'delete' | undefined

function Lead({ leadId }: { leadId: string }) {
  const navigate = useNavigate()
  const toast = useToast()
  const [form, setForm] = useState<Partial<LeadDetail>>({})
  const [saving, setSaving] = useState(false)
  const [dialog, setDialog] = useState<Pending>()

  const detail = useAsync<LeadDetail>(
    (signal) =>
      api.get<LeadDetail>(`/leads/${leadId}`, undefined, signal).then((loaded) => {
        setForm(loaded)
        return loaded
      }),
    [leadId],
  )

  const apply = useCallback((values: Partial<LeadDetail>) => setForm(values), [])
  const changes = useUnsavedChanges(form, detail.data, apply, EDITED)
  const guard = useLeaveGuard(changes)

  function set<K extends keyof LeadDetail>(key: K, value: LeadDetail[K]) {
    setForm((current) => ({ ...current, [key]: value }))
  }

  async function save() {
    setSaving(true)
    try {
      await api.patch(`/leads/${leadId}`, {
        fullName: form.fullName,
        companyName: form.companyName ?? '',
        email: form.email ?? '',
        phone: form.phone ?? '',
        jobTitle: form.jobTitle ?? '',
        message: form.message ?? '',
        leadSourceId: form.leadSourceId,

        // A picklist is cleared by asking for it; a null id alone means "unchanged".
        clearLeadSource: form.leadSourceId === null,
      })
      changes.accept()
      toast.show('Lead saved', 'success')
      detail.reload()
    } catch (cause) {
      toast.show(cause instanceof ApiError ? cause.message : 'Could not save', 'error')
    } finally {
      setSaving(false)
    }
  }

  if (detail.loading && !detail.data) return <Loading />
  if (detail.error) return <ErrorState title={detail.error.title} detail={detail.error.detail} onRetry={detail.reload} />
  if (!detail.data) return null

  const lead = detail.data
  const open = isOpenLead(lead.status)

  function done(message: string) {
    setDialog(undefined)
    toast.show(message, 'success')
    detail.reload()
  }

  return (
    <>
      <PageHeader
        eyebrow={`${lead.countryIsoCode} · ${CHANNEL_LABELS[lead.channel]} lead${lead.isTest ? ' · TEST - counted nowhere' : ''}`}
        title={lead.fullName}
        back={{ label: 'Back to leads', onClick: () => navigate('/leads') }}
        actions={
          <>
            {lead.canDelete && (
              <Button size="sm" variant="danger" onClick={() => setDialog('delete')}>
                Delete
              </Button>
            )}
            {lead.canAssign && (
              <Button size="sm" onClick={() => setDialog('assign')}>
                Reassign
              </Button>
            )}
            {lead.canEdit && (
              <Button size="sm" onClick={() => setDialog('status')}>
                Change status
              </Button>
            )}
            {(lead.canEdit || lead.status === 'Converted') && (
              <Button size="sm" onClick={() => setDialog('activity')}>
                Log activity
              </Button>
            )}
            {lead.canConvert && (
              <Button size="sm" variant="primary" onClick={() => setDialog('convert')}>
                Convert to customer
              </Button>
            )}
          </>
        }
      />

      <div className="mb-5 flex flex-wrap items-center gap-2">
        <Badge tone={leadStatusTone(lead.status)}>{lead.status}</Badge>
        {lead.tier && <Badge tone={tierTone(lead.tier)}>{TIER_LABELS[lead.tier]}</Badge>}
        <span className="text-[12.5px] text-muted">
          {lead.ownerName
            ? `Owned by ${lead.ownerName}`
            : lead.assignmentPending
              ? 'Not assigned yet - the company is being researched first, and its tier decides who gets it'
              : 'Unassigned - in the country queue'}
          {lead.firstRespondedAt
            ? ` · first answered ${dateTime(lead.firstRespondedAt)}`
            : open && ' · not answered yet'}
        </span>
        {lead.status === 'Disqualified' && lead.disqualifyReasonName && (
          <span className="text-[12.5px] text-muted">
            {'·'} {lead.disqualifyReasonName}
            {lead.statusNote && ` - ${lead.statusNote}`}
          </span>
        )}
      </div>

      {lead.status === 'Converted' && (
        <div className="mb-5 rounded-md border border-pos/30 bg-pos-wash px-4 py-3 text-[13px] text-ink-2">
          Converted {dateTime(lead.convertedAt)} into{' '}
          {lead.convertedCustomerId ? (
            <Link to={`/customers/${lead.convertedCustomerId}`} className="font-medium text-accent hover:underline">
              {lead.convertedCustomerName ?? 'the customer'}
            </Link>
          ) : (
            'a customer'
          )}
          {lead.convertedQuotationId && (
            <>
              {', with '}
              <Link to={`/quotations/${lead.convertedQuotationId}`} className="font-medium text-accent hover:underline">
                a draft quotation
              </Link>
            </>
          )}
          . The lead stays as the record of where the account came from.
        </div>
      )}

      <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_380px]">
        <div className="flex min-w-0 flex-col gap-5">
          {(lead.profile || lead.tier) && <CompanyCard lead={lead} onResearched={detail.reload} />}

          <Card>
            <CardHeader title="Who they are" hint={lead.canEdit ? undefined : 'Read-only.'} />
            <FormGrid>
              <Field label="Name" required changed={changes.changed('fullName')}>
                {(id) => (
                  <Input id={id} disabled={!lead.canEdit} value={form.fullName ?? ''} onChange={(e) => set('fullName', e.target.value)} />
                )}
              </Field>
              <Field label="Company" changed={changes.changed('companyName')}>
                {(id) => (
                  <Input id={id} disabled={!lead.canEdit} value={form.companyName ?? ''} onChange={(e) => set('companyName', e.target.value)} />
                )}
              </Field>
              <Field label="Email" changed={changes.changed('email')}>
                {(id) => (
                  <Input id={id} type="email" disabled={!lead.canEdit} value={form.email ?? ''} onChange={(e) => set('email', e.target.value)} />
                )}
              </Field>
              <Field label="Phone" changed={changes.changed('phone')}>
                {(id) => (
                  <Input id={id} type="tel" disabled={!lead.canEdit} value={form.phone ?? ''} onChange={(e) => set('phone', e.target.value)} />
                )}
              </Field>
              <Field label="Job title" changed={changes.changed('jobTitle')}>
                {(id) => (
                  <Input id={id} disabled={!lead.canEdit} value={form.jobTitle ?? ''} onChange={(e) => set('jobTitle', e.target.value)} />
                )}
              </Field>
              <PicklistField
                label="Source of lead"
                list="LeadSource"
                countryId={lead.countryId}
                value={form.leadSourceId ?? null}
                valueName={lead.leadSourceName}
                onChange={(next) => set('leadSourceId', next)}
                disabled={!lead.canEdit}
                changed={changes.changed('leadSourceId')}
              />
              <div className="sm:col-span-2">
                <Field label="What they asked about" changed={changes.changed('message')}>
                  {(id) => (
                    <Textarea id={id} rows={3} disabled={!lead.canEdit} value={form.message ?? ''} onChange={(e) => set('message', e.target.value)} />
                  )}
                </Field>
              </div>
            </FormGrid>
            {lead.canEdit && (
              <SaveActions
                count={changes.fields.length}
                saving={saving}
                saveLabel="Save lead"
                canSave={Boolean(form.fullName?.trim()) && Boolean(form.email?.trim() || form.phone?.trim())}
                onSave={() => void save()}
                onDiscard={changes.discard}
              />
            )}
          </Card>

          {lead.answers.length > 0 && (
            <Card>
              <CardHeader title="Their other answers" hint="Labelled as the question was asked on the version they answered." />
              <dl className="grid gap-x-6 gap-y-3 px-5 pb-5 sm:grid-cols-2">
                {lead.answers.map((answer) => (
                  <div key={answer.key}>
                    <dt className="text-[12px] text-muted">{answer.label}</dt>
                    <dd className="text-sm">{answer.value}</dd>
                  </div>
                ))}
              </dl>
            </Card>
          )}

          <SourceCard lead={lead} />
        </div>

        <Timeline activities={lead.activities} />
      </div>

      <LeaveGuard guard={guard} />

      <LogActivityDialog
        open={dialog === 'activity'}
        leadId={leadId}
        onClose={() => setDialog(undefined)}
        onDone={() => done('Logged')}
      />
      <StatusDialog
        open={dialog === 'status'}
        lead={lead}
        onClose={() => setDialog(undefined)}
        onDone={(status) => done(`Moved to ${status}`)}
      />
      <AssignDialog
        open={dialog === 'assign'}
        lead={lead}
        onClose={() => setDialog(undefined)}
        onDone={() => done('Reassigned')}
      />
      <ConvertDialog
        open={dialog === 'convert'}
        lead={lead}
        onClose={() => setDialog(undefined)}
        onConverted={(result) => {
          toast.show(`${lead.fullName} converted`, 'success')
          guard.bypass(() =>
            navigate(result.quotationId ? `/quotations/${result.quotationId}` : `/customers/${result.customerId}`),
          )
        }}
      />
      <DeleteDialog
        open={dialog === 'delete'}
        lead={lead}
        onClose={() => setDialog(undefined)}
        onDeleted={() => {
          toast.show(`${lead.fullName} deleted`, 'success')
          guard.bypass(() => navigate('/leads', { replace: true }))
        }}
      />
    </>
  )
}

/**
 * Where they came from, and what they agreed to.
 *
 * <p>
 * The consent wording is shown exactly as the person saw it, because that is what was
 * stored - a pointer to wording that has since been edited would prove nothing.
 * </p>
 */
/**
 * What research found about the company, and the tier QuoteHero's rules set from it.
 *
 * <p>
 * A suggestion, like a scanned namecard: every fact shows where it came from so a rep can
 * check "14 outlets" in one click rather than take a model's word for it, and an Unknown stays
 * on the card because it is the first question on the call. Nothing here is copied onto a
 * customer until somebody converts the lead and confirms it.
 * </p>
 */
function CompanyCard({ lead, onResearched }: { lead: LeadDetail; onResearched: () => void }) {
  const toast = useToast()
  const [asking, setAsking] = useState(false)
  const profile = lead.profile

  // Researching is usually done inside two minutes. One look a few seconds after asking
  // catches the quick case; the page is not left polling for the slow one.
  useEffect(() => {
    if (!asking) return
    const timer = window.setTimeout(() => {
      setAsking(false)
      onResearched()
    }, 5000)
    return () => window.clearTimeout(timer)
  }, [asking, onResearched])

  async function researchAgain() {
    try {
      await api.post(`/leads/${lead.id}/profile`)
      toast.show('Researching the company again', 'success')
      setAsking(true)
      onResearched()
    } catch (cause) {
      toast.show(cause instanceof ApiError ? cause.message : 'Could not start the research', 'error')
    }
  }

  const facts = profile?.status === 'Completed' ? profileFacts(profile) : []
  const sources = (profile?.sources ?? [])
    .map((source) => ({ ...source, href: safeSourceHref(source.url) }))
    .filter((source) => source.href)

  return (
    <Card>
      <CardHeader
        title="Company"
        hint="Researched from public sources. Check a fact against its source before relying on it."
        action={
          lead.canEdit && profile?.status !== 'Pending' ? (
            <Button size="sm" onClick={researchAgain} disabled={asking}>
              Research again
            </Button>
          ) : undefined
        }
      />

      <div className="flex flex-col gap-4 px-5 pb-5 text-[13px]">
        {lead.tier && (
          <div className="flex flex-wrap items-baseline gap-2">
            <Badge tone={tierTone(lead.tier)}>{TIER_LABELS[lead.tier]}</Badge>
            {lead.tierReason && <span className="text-ink-2">Why: {lead.tierReason}</span>}
          </div>
        )}

        {profile?.status === 'Pending' && (
          <p className="rounded-md border border-line-soft bg-wash px-3 py-2 text-muted">
            Researching the company before assigning - usually under two minutes.
            {profile.attempts > 0 && ` Retrying (attempt ${profile.attempts + 1}).`}
          </p>
        )}

        {profile?.status === 'Failed' && (
          <p className="text-muted">
            The research did not finish after {profile.attempts} attempts, so the lead went on as Standard.
            {profile.lastError && <span className="block text-[12px] text-faint">Last error: {profile.lastError}</span>}
          </p>
        )}

        {profile?.status === 'Skipped' && (
          <p className="text-muted">Nothing to research: no company name and no company email address.</p>
        )}

        {profile?.status === 'Completed' && (
          <>
            {profile.notABusiness && (
              <p className="rounded-md border border-warn/30 bg-warn-wash px-3 py-2 text-ink-2">
                Looks like this is not a business enquiry{profile.notABusinessReason ? `: ${profile.notABusinessReason}` : '.'}
              </p>
            )}

            <dl className="grid grid-cols-[max-content_minmax(0,1fr)] gap-x-6 gap-y-2">
              {facts.map((fact) => (
                <div key={fact.label} className="contents">
                  <dt className="text-muted">{fact.label}</dt>
                  <dd className={cx('break-words', fact.unknown && 'text-warn')}>
                    {fact.href ? (
                      <a href={fact.href} target="_blank" rel="noopener noreferrer" className="text-accent hover:underline">
                        {fact.value}
                      </a>
                    ) : (
                      fact.value
                    )}
                  </dd>
                </div>
              ))}
            </dl>

            {profile.summary && <p className="text-ink-2">{profile.summary}</p>}

            {sources.length > 0 && (
              <div>
                <p className="font-mono text-[11px] uppercase tracking-[0.12em] text-muted">Sources</p>
                <ol className="mt-1.5 list-decimal space-y-1 pl-5">
                  {sources.map((source, index) => (
                    <li key={`${source.url}-${index}`}>
                      <a
                        href={source.href!}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-accent hover:underline"
                      >
                        {source.fact}
                      </a>
                    </li>
                  ))}
                </ol>
              </div>
            )}
          </>
        )}

        <p className="text-[11.5px] text-faint">
          A suggestion, like a scanned namecard - nothing here reaches a customer record until somebody converts the
          lead and confirms it. The tier is set by QuoteHero's rules from these facts, not by the research itself.
          {profile?.completedAt && ` Researched ${dateTime(profile.completedAt)}.`}
        </p>
      </div>
    </Card>
  )
}

function SourceCard({ lead }: { lead: LeadDetail }) {
  const rows: [string, string | null][] = [
    ['Channel', CHANNEL_LABELS[lead.channel]],
    [
      'Form',
      lead.leadFormName
        ? `${lead.leadFormName}${lead.leadFormVersionNo ? ` (version ${lead.leadFormVersionNo})` : ''}`
        : null,
    ],
    ['Ad connection', lead.adLeadSourceName],
    ['Arrived', dateTime(lead.createdAt) + (lead.createdByName ? ` by ${lead.createdByName}` : '')],
    ['Ad campaign', lead.campaignName ?? lead.campaignId],
    ['Ad', lead.adName ?? lead.adId],
    [
      lead.channel === 'MetaAds' ? 'Meta lead id' : lead.channel === 'GoogleAds' ? 'Google lead id' : 'Platform lead id',
      lead.channel === 'MetaAds' || lead.channel === 'GoogleAds' ? lead.externalLeadId : null,
    ],
    ['Test lead', lead.isTest ? 'Yes - from a source marked as a test, counted nowhere' : null],
    ['Campaign', lead.utmCampaign],
    ['Source / medium', [lead.utmSource, lead.utmMedium].filter(Boolean).join(' / ') || null],
    ['Term', lead.utmTerm],
    ['Content', lead.utmContent],
    ['Google click id', lead.gclid],
    ['Meta click id', lead.fbclid],
    ['Landing page', lead.landingPageUrl],
    ['Referrer', lead.referrer],
  ]

  return (
    <Card>
      <CardHeader title="Where they came from" />
      <dl className="grid grid-cols-[max-content_minmax(0,1fr)] gap-x-6 gap-y-2 px-5 pb-5 text-[13px]">
        {rows
          .filter(([, value]) => value)
          .map(([label, value]) => (
            <div key={label} className="contents">
              <dt className="text-muted">{label}</dt>
              <dd className="break-words">{value}</dd>
            </div>
          ))}
      </dl>
      {lead.consentText && (
        <div className="border-t border-line-soft px-5 py-4 text-[13px]">
          <p className="font-mono text-[11px] uppercase tracking-[0.12em] text-muted">
            Agreed {dateTime(lead.consentedAt)}
          </p>
          <p className="mt-1.5 text-ink-2">{lead.consentText}</p>
        </div>
      )}
    </Card>
  )
}

const ACTIVITY_LABELS: Record<LeadActivityType, string> = {
  Note: 'Note',
  Call: 'Call',
  Email: 'Email',
  Meeting: 'Meeting',
  StatusChanged: 'Status',
  Assigned: 'Assigned',
  Submitted: 'Arrived',
  Merged: 'Enquired again',
  Converted: 'Converted',
}

/** What was done about this lead, newest first. The audit trail says what changed; this says what we did. */
function Timeline({ activities }: { activities: LeadActivityItem[] }) {
  return (
    <Card className="self-start">
      <CardHeader title="Timeline" />
      {activities.length === 0 ? (
        <EmptyState title="Nothing yet" hint="Log a call, an email or a note once you have made contact." />
      ) : (
        <ol className="flex flex-col gap-4 px-5 pb-5">
          {activities.map((activity) => (
            <li key={activity.id} className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-3">
              <span
                aria-hidden
                className={cx('mt-1.5 size-2 rounded-full', activity.isSystem ? 'bg-line' : 'bg-accent')}
              />
              <div>
                <p className="text-[12px] text-muted">
                  <span className="font-medium text-ink-2">{ACTIVITY_LABELS[activity.type]}</span>
                  {' · '}
                  {dateTime(activity.occurredAt)}
                  {activity.actorName && ` · ${activity.actorName}`}
                </p>
                {activity.body && <p className="mt-0.5 whitespace-pre-line text-[13px] text-ink-2">{activity.body}</p>}
              </div>
            </li>
          ))}
        </ol>
      )}
    </Card>
  )
}

function LogActivityDialog({
  open,
  leadId,
  onClose,
  onDone,
}: {
  open: boolean
  leadId: string
  onClose: () => void
  onDone: () => void
}) {
  const toast = useToast()
  const [type, setType] = useState<LeadActivityType>('Call')
  const [body, setBody] = useState('')
  const [saving, setSaving] = useState(false)

  async function submit() {
    setSaving(true)
    try {
      await api.post(`/leads/${leadId}/activities`, { type, body: body || null })
      setBody('')
      onDone()
    } catch (cause) {
      toast.show(cause instanceof ApiError ? cause.message : 'Could not log that', 'error')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog
      open={open}
      title="Log activity"
      description="Logging something against a new lead marks it answered and moves it to Working."
      onClose={onClose}
      footer={
        <>
          <Button size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button size="sm" variant="primary" loading={saving} disabled={type === 'Note' && !body.trim()} onClick={() => void submit()}>
            Log it
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Field label="What happened">
          {(id) => (
            <Select id={id} value={type} onChange={(e) => setType(e.target.value as LeadActivityType)}>
              <option value="Call">A call</option>
              <option value="Email">An email</option>
              <option value="Meeting">A meeting</option>
              <option value="Note">A note</option>
            </Select>
          )}
        </Field>
        <Field label="Details" hint={type === 'Note' ? 'Required for a note.' : 'Optional - what was said, what happens next.'}>
          {(id) => <Textarea id={id} rows={4} maxLength={2000} value={body} onChange={(e) => setBody(e.target.value)} />}
        </Field>
      </div>
    </Dialog>
  )
}

/**
 * Moving a lead along, or closing it.
 *
 * <p>
 * Disqualified needs a reason from the list: "we looked and they were not a fit" only counts
 * for something if it says why. Junk needs none - it is in no figure anywhere.
 * </p>
 */
function StatusDialog({
  open,
  lead,
  onClose,
  onDone,
}: {
  open: boolean
  lead: LeadDetail
  onClose: () => void
  onDone: (status: LeadStatus) => void
}) {
  const toast = useToast()
  const leadIsOpen = isOpenLead(lead.status)

  const choices = useMemo<{ status: LeadStatus; label: string }[]>(() => {
    if (!leadIsOpen) return [{ status: 'Working', label: 'Reopen - back to Working' }]

    const moves: { status: LeadStatus; label: string }[] = [
      { status: 'Working', label: 'Working - contact made or tried' },
      { status: 'Qualified', label: 'Qualified - a real need, worth a quotation' },
    ]

    if (lead.canDisqualify) {
      moves.push({ status: 'Disqualified', label: 'Disqualified - a real person, not a fit' })
      moves.push({ status: 'Junk', label: 'Junk - spam, a test, a job seeker' })
    }

    return moves.filter((move) => move.status !== lead.status)
  }, [leadIsOpen, lead.canDisqualify, lead.status])

  const [status, setStatus] = useState<LeadStatus | ''>('')
  const [reasonId, setReasonId] = useState('')
  const [note, setNote] = useState('')
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (open) {
      setStatus(choices[0]?.status ?? '')
      setReasonId('')
      setNote('')
    }
  }, [open, choices])

  const reasons = useAsync<ReferenceItem[]>(
    (signal) =>
      open && lead.canDisqualify
        ? api.get<ReferenceItem[]>('/reference/DisqualifyReason', { countryId: lead.countryId }, signal)
        : Promise.resolve([]),
    [open, lead.canDisqualify, lead.countryId],
  )

  async function submit() {
    if (!status) return

    setSaving(true)
    try {
      await api.post(`/leads/${lead.id}/status`, {
        status,
        disqualifyReasonId: status === 'Disqualified' ? reasonId : null,
        note: note || null,
      })
      onDone(status)
    } catch (cause) {
      toast.show(cause instanceof ApiError ? cause.message : 'Could not change the status', 'error')
    } finally {
      setSaving(false)
    }
  }

  const closing = status === 'Disqualified' || status === 'Junk'

  return (
    <Dialog
      open={open}
      title="Change status"
      description={`${lead.fullName} is ${lead.status}. Converting is its own button - it creates the customer.`}
      onClose={onClose}
      loading={reasons.loading}
      footer={
        <>
          <Button size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            size="sm"
            variant={closing ? 'danger' : 'primary'}
            loading={saving}
            disabled={!status || (status === 'Disqualified' && !reasonId)}
            onClick={() => void submit()}
          >
            {status ? `Move to ${status}` : 'Move'}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <fieldset className="flex flex-col gap-2">
          <legend className="mb-1 text-[13px] font-medium text-ink-2">Move to</legend>
          {choices.map((choice) => (
            <label key={choice.status} className="flex cursor-pointer items-center gap-2 text-sm">
              <input
                type="radio"
                name="lead-status"
                className="accent-[var(--accent)]"
                checked={status === choice.status}
                onChange={() => setStatus(choice.status)}
              />
              {choice.label}
            </label>
          ))}
        </fieldset>

        {status === 'Disqualified' && (
          <Field label="Why" required>
            {(id) => (
              <Select id={id} value={reasonId} onChange={(e) => setReasonId(e.target.value)}>
                <option value="">Choose a reason</option>
                {(reasons.data ?? []).map((reason) => (
                  <option key={reason.id} value={reason.id}>
                    {reason.name}
                  </option>
                ))}
              </Select>
            )}
          </Field>
        )}

        {closing && (
          <Field label="Note" hint="Optional. Shown on the lead beside its status.">
            {(id) => <Textarea id={id} rows={2} maxLength={1000} value={note} onChange={(e) => setNote(e.target.value)} />}
          </Field>
        )}
      </div>
    </Dialog>
  )
}

function AssignDialog({
  open,
  lead,
  onClose,
  onDone,
}: {
  open: boolean
  lead: LeadDetail
  onClose: () => void
  onDone: () => void
}) {
  const toast = useToast()
  const [ownerUserId, setOwnerUserId] = useState('')
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (open) setOwnerUserId(lead.ownerUserId ?? '')
  }, [open, lead.ownerUserId])

  const people = useAsync<LeadAssignee[]>(
    (signal) =>
      open ? api.get<LeadAssignee[]>('/leads/assignees', { countryId: lead.countryId }, signal) : Promise.resolve([]),
    [open, lead.countryId],
  )

  async function submit() {
    setSaving(true)
    try {
      await api.post(`/leads/${lead.id}/assign`, { ownerUserId: ownerUserId || null })
      onDone()
    } catch (cause) {
      toast.show(cause instanceof ApiError ? cause.message : 'Could not reassign', 'error')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog
      open={open}
      title="Reassign lead"
      description="The new owner is told straight away."
      onClose={onClose}
      loading={people.loading}
      error={people.error}
      onRetry={people.reload}
      footer={
        <>
          <Button size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            size="sm"
            variant="primary"
            loading={saving}
            disabled={ownerUserId === (lead.ownerUserId ?? '')}
            onClick={() => void submit()}
          >
            Reassign
          </Button>
        </>
      }
    >
      <Field label="Owner" hint="Only people you may assign to, who can open leads in this country.">
        {(id) => (
          <Select id={id} value={ownerUserId} onChange={(e) => setOwnerUserId(e.target.value)}>
            <option value="">Nobody - back to the unassigned queue</option>
            {lead.ownerUserId && !(people.data ?? []).some((person) => person.userId === lead.ownerUserId) && (
              <option value={lead.ownerUserId}>{lead.ownerName ?? 'Current owner'}</option>
            )}
            {(people.data ?? []).map((person) => (
              <option key={person.userId} value={person.userId}>
                {person.displayName}
              </option>
            ))}
          </Select>
        )}
      </Field>
    </Dialog>
  )
}

/**
 * The one door out of a lead: into a customer, a contact, and optionally a draft quotation.
 *
 * <p>
 * Match first. The customers this lead might already be are offered before "create a new
 * one", because without that step a CRM produces four copies of every account within a year.
 * </p>
 */
function ConvertDialog({
  open,
  lead,
  onClose,
  onConverted,
}: {
  open: boolean
  lead: LeadDetail
  onClose: () => void
  onConverted: (result: ConvertLeadResult) => void
}) {
  const { can } = useAuth()
  const toast = useToast()
  const canCreateCustomer = can('customer.create')
  const canQuote = can('quotation.create')

  /** A customer id to attach to, or 'new'. */
  const [target, setTarget] = useState<string>('new')
  const [contactId, setContactId] = useState('new')
  const [customer, setCustomer] = useState({ companyName: '', registrationNo: '', industry: '', website: '' })
  const [businessTypeId, setBusinessTypeId] = useState<string | null>(null)
  const [contact, setContact] = useState({ name: '', jobTitle: '', email: '', phone: '' })
  const [createQuotation, setCreateQuotation] = useState(true)
  const [saving, setSaving] = useState(false)

  const matches = useAsync<LeadCustomerMatch[]>(
    (signal) => (open ? api.get<LeadCustomerMatch[]>(`/leads/${lead.id}/matches`, undefined, signal) : Promise.resolve([])),
    [open, lead.id],
  )

  // Seeded each time it opens, from the lead as it is now - and pointed at the first match
  // when there is one, because that is the answer the step exists to suggest.
  useEffect(() => {
    if (!open) return

    setCustomer({ companyName: lead.companyName ?? '', registrationNo: '', industry: '', website: '' })
    setBusinessTypeId(null)
    setContact({
      name: lead.fullName,
      jobTitle: lead.jobTitle ?? '',
      email: lead.email ?? '',
      phone: lead.phone ?? '',
    })
    setCreateQuotation(canQuote)
  }, [open, lead, canQuote])

  useEffect(() => {
    if (!open || !matches.data) return

    const first = matches.data[0]
    setTarget(first ? first.customerId : canCreateCustomer ? 'new' : '')
  }, [open, matches.data, canCreateCustomer])

  const attaching = matches.data?.find((match) => match.customerId === target)

  // The contact already on the customer with this lead's address is the person, so it is
  // the default rather than a second copy of them.
  useEffect(() => {
    if (!attaching) {
      setContactId('new')
      return
    }

    const same = attaching.contacts.find(
      (option) => option.email && lead.email && option.email.toLowerCase() === lead.email.toLowerCase(),
    )
    setContactId(same?.id ?? 'new')
  }, [attaching, lead.email])

  async function submit() {
    setSaving(true)
    try {
      const result = await api.post<ConvertLeadResult>(`/leads/${lead.id}/convert`, {
        customerId: attaching ? attaching.customerId : null,
        newCustomer: attaching
          ? null
          : {
              companyName: customer.companyName,
              registrationNo: customer.registrationNo || null,
              industry: customer.industry || null,
              website: customer.website || null,
              businessTypeId,
            },
        contactId: attaching && contactId !== 'new' ? contactId : null,
        newContact:
          attaching && contactId !== 'new'
            ? null
            : {
                name: contact.name,
                jobTitle: contact.jobTitle || null,
                email: contact.email || null,
                phone: contact.phone || null,
              },
        createQuotation: canQuote && createQuotation,
      })
      onConverted(result)
    } catch (cause) {
      toast.show(cause instanceof ApiError ? cause.message : 'Could not convert the lead', 'error')
    } finally {
      setSaving(false)
    }
  }

  const ready =
    target !== '' &&
    (attaching ? true : customer.companyName.trim() !== '') &&
    (attaching && contactId !== 'new' ? true : contact.name.trim() !== '')

  return (
    <Dialog
      open={open}
      wide
      title={`Convert ${lead.fullName}`}
      description="The lead closes as Converted and stays as the record of where the account came from."
      onClose={onClose}
      loading={matches.loading}
      error={matches.error}
      onRetry={matches.reload}
      footer={
        <>
          <Button size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button size="sm" variant="primary" loading={saving} disabled={!ready} onClick={() => void submit()}>
            {attaching ? `Attach to ${attaching.companyName}` : 'Create customer'}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-5">
        <fieldset className="flex flex-col gap-2">
          <legend className="mb-1 text-[13px] font-medium text-ink-2">Which customer is this?</legend>

          {(matches.data ?? []).length === 0 && (
            <p className="text-[13px] text-muted">
              No customer in {lead.countryIsoCode} shares this company name, email address or email domain.
            </p>
          )}

          {(matches.data ?? []).map((match) => (
            <label
              key={match.customerId}
              className={cx(
                'flex cursor-pointer items-start gap-2.5 rounded-md border px-3 py-2.5 text-sm',
                target === match.customerId ? 'border-accent bg-accent-wash' : 'border-line',
              )}
            >
              <input
                type="radio"
                name="convert-target"
                className="mt-1 accent-[var(--accent)]"
                checked={target === match.customerId}
                onChange={() => setTarget(match.customerId)}
              />
              <span>
                <span className="font-medium">{match.companyName}</span>
                <span className="block text-[12px] text-muted">
                  {match.reason}
                  {match.registrationNo && ` · ${match.registrationNo}`}
                  {match.ownerName && ` · ${match.ownerName}'s`}
                  {match.status !== 'Active' && ` · ${match.status.toLowerCase()} - cannot take new work`}
                </span>
              </span>
            </label>
          ))}

          <label
            className={cx(
              'flex items-start gap-2.5 rounded-md border px-3 py-2.5 text-sm',
              canCreateCustomer ? 'cursor-pointer' : 'opacity-60',
              target === 'new' ? 'border-accent bg-accent-wash' : 'border-line',
            )}
          >
            <input
              type="radio"
              name="convert-target"
              className="mt-1 accent-[var(--accent)]"
              disabled={!canCreateCustomer}
              checked={target === 'new'}
              onChange={() => setTarget('new')}
            />
            <span>
              <span className="font-medium">A new customer</span>
              <span className="block text-[12px] text-muted">
                {canCreateCustomer
                  ? 'Created from the lead, owned by whoever owns the lead.'
                  : 'You do not have permission to create customers.'}
              </span>
            </span>
          </label>
        </fieldset>

        {!attaching && target === 'new' && (
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Company name" required>
              {(id) => (
                <Input id={id} value={customer.companyName} onChange={(e) => setCustomer({ ...customer, companyName: e.target.value })} />
              )}
            </Field>
            <PicklistField
              label="Business type"
              list="BusinessType"
              countryId={lead.countryId}
              value={businessTypeId}
              onChange={setBusinessTypeId}
            />
            <Field label="Registration number">
              {(id) => (
                <Input id={id} value={customer.registrationNo} onChange={(e) => setCustomer({ ...customer, registrationNo: e.target.value })} />
              )}
            </Field>
            <Field label="Industry">
              {(id) => (
                <Input id={id} value={customer.industry} onChange={(e) => setCustomer({ ...customer, industry: e.target.value })} />
              )}
            </Field>
          </div>
        )}

        {attaching && (
          <Field label="Contact" hint="The person on the customer this lead is.">
            {(id) => (
              <Select id={id} value={contactId} onChange={(e) => setContactId(e.target.value)}>
                <option value="new">A new contact, from this lead</option>
                {attaching.contacts.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.name}
                    {option.email ? ` (${option.email})` : ''}
                    {option.isPrimary ? ' - primary' : ''}
                  </option>
                ))}
              </Select>
            )}
          </Field>
        )}

        {(!attaching || contactId === 'new') && target !== '' && (
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Contact name" required>
              {(id) => <Input id={id} value={contact.name} onChange={(e) => setContact({ ...contact, name: e.target.value })} />}
            </Field>
            <Field label="Job title">
              {(id) => <Input id={id} value={contact.jobTitle} onChange={(e) => setContact({ ...contact, jobTitle: e.target.value })} />}
            </Field>
            <Field label="Email">
              {(id) => <Input id={id} type="email" value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} />}
            </Field>
            <Field label="Phone">
              {(id) => <Input id={id} type="tel" value={contact.phone} onChange={(e) => setContact({ ...contact, phone: e.target.value })} />}
            </Field>
          </div>
        )}

        {canQuote && (
          <Checkbox
            label="Start a draft quotation for them"
            checked={createQuotation}
            onChange={(e) => setCreateQuotation(e.target.checked)}
          />
        )}
      </div>
    </Dialog>
  )
}

function DeleteDialog({
  open,
  lead,
  onClose,
  onDeleted,
}: {
  open: boolean
  lead: LeadDetail
  onClose: () => void
  onDeleted: () => void
}) {
  const toast = useToast()
  const [deleting, setDeleting] = useState(false)

  async function remove() {
    setDeleting(true)
    try {
      await api.del(`/leads/${lead.id}`)
      onDeleted()
    } catch (cause) {
      toast.show(cause instanceof ApiError ? cause.message : 'Could not delete the lead', 'error')
      setDeleting(false)
    }
  }

  return (
    <Dialog
      open={open}
      title={`Delete ${lead.fullName}?`}
      onClose={onClose}
      footer={
        <>
          <Button size="sm" onClick={onClose}>
            Keep it
          </Button>
          <Button size="sm" variant="danger" loading={deleting} onClick={() => void remove()}>
            Delete for good
          </Button>
        </>
      }
    >
      <p className="text-[13px] text-ink-2">
        The lead, its timeline and the raw submission it arrived as are removed for good. This is
        for spam, tests, and somebody who has withdrawn their consent to be contacted. To close a
        real lead, disqualify it instead - that keeps it in the figures.
      </p>
    </Dialog>
  )
}
